// Bilingual content for NEXA Real Estate OS product landing page.
// Kept as a separate module to keep the page component focused on layout & interaction.

export const REO_CONTENT = {
  ar: {
    slug: 'nexa-real-estate-os',
    productName: 'NEXA Real Estate OS',
    backToProducts: 'العودة للمنتجات',

    // Hero
    hero: {
      overline: 'منصة العقارات المتصلة',
      title: 'حوّل مشروعك العقاري إلى تجربة بيع وتشغيل متصلة',
      text: 'تربط NEXA Real Estate OS الخريطة والمشروع والطابق والوحدة بالاستفسارات والمبيعات والتأجير وإدارة الأملاك، لتعمل فرقك وعملاؤك على معلومات واضحة ومحدثة.',
      cta1: 'اطلب تصورًا لمشروعك',
      cta2: 'اكتشف كيف تعمل المنصة',
      chipMap: 'خريطة المشروع',
      chipUnit: 'وحدة A-201',
      chipStatus: 'متاحة',
    },

    // Problem
    problem: {
      overline: 'المشكلة',
      title: 'العميل يرى مشروعًا… بينما الشركة تدير أجزاءً منفصلة',
      lead: 'عندما يكون الموقع منفصلًا عن المخزون، والمخططات في ملفات، والأسعار في جداول، والاستفسارات في المحادثات؛ تضيع علاقة العميل بالوحدة ويصعب معرفة ما يحدث فعليًا.',
      chain: [
        'موقع تعريفي',
        'مخطط PDF',
        'ملف وحدات وأسعار',
        'استفسارات واتساب',
        'أنظمة بيع وتأجير وتشغيل منفصلة',
      ],
      punchline: 'المشكلة ليست نقص البيانات؛ بل غياب الرحلة التي تربطها.',
    },

    // Core Idea
    idea: {
      overline: 'الفكرة الأساسية',
      title: 'من الخريطة إلى العقد… سياق واحد لا ينقطع',
      journey: [
        'الخريطة',
        'المشروع',
        'المبنى والطابق',
        'الوحدة',
        'الاستفسار والزيارة',
        'العرض والحجز',
        'العقد والتحصيل',
        'الصيانة وتقارير المالك',
      ],
      text: 'يحمل كل طلب تفاصيل المشروع والوحدة والمصدر منذ لحظة اختيار العميل، ويستمر هذا السياق مع فرق المبيعات والتأجير وإدارة الأملاك.',
    },

    // Search Experience
    search: {
      overline: 'تجربة العميل',
      title: 'بدل أن يسأل العميل «ما المتاح؟»… يختار وحدته وهو يفهمها',
      mapLabel: 'موقع توضيحي — حي النرجس، الرياض',
      mapCaption: 'بيانات توضيحية',
      unit: {
        code: 'A-201',
        rooms: '٣ غرف',
        area: '148 م²',
        direction: 'شمال شرقي',
        price: '1,420,000 ر.س',
        status: 'متاحة',
      },
      hint: 'مرّر على الوحدة لرؤية تفاصيلها',
      hintMobile: 'اضغط الوحدة لرؤية تفاصيلها',
    },

    // Inquiry with context
    inquiry: {
      overline: 'الاستفسارات',
      title: 'فريق المبيعات يعرف ماذا اختار العميل قبل أول اتصال',
      fields: [
        { label: 'العميل والمصدر', value: 'محمد الحربي — إعلان Google Ads' },
        { label: 'المشروع', value: 'سدرة سكوير — النرجس' },
        { label: 'المبنى والطابق', value: 'A — الطابق الثاني' },
        { label: 'الوحدة', value: 'A-201 (شمال شرقي)' },
        { label: 'السعر والمساحة', value: '1,420,000 ر.س — 148 م²' },
        { label: 'الوحدات التي شاهدها', value: 'A-201، A-305، B-102' },
        { label: 'المسؤول', value: 'سارة العتيبي' },
        { label: 'الإجراء التالي', value: 'زيارة معاينة — الخميس ٥:٠٠م' },
      ],
      valueTitle: 'ماذا يعني ذلك عمليًا؟',
      values: [
        'متابعة أوضح للفريق والعميل.',
        'توزيع أدق للعملاء بحسب المشروع والمصدر.',
        'معرفة المصادر التي تجلب الفرص الفعلية.',
        'تقليل تضارب حالة الوحدة بين الفرق.',
      ],
    },

    // Sales & Leasing tracks
    tracks: {
      overline: 'البيع والتأجير',
      title: 'الوحدة نفسها تنتقل من التسويق إلى العقد والتحصيل',
      sale: {
        label: 'مسار البيع',
        steps: ['استفسار', 'تأهيل', 'زيارة', 'عرض', 'حجز', 'عقد', 'تسليم'],
      },
      lease: {
        label: 'مسار التأجير',
        steps: ['طلب', 'مراجعة', 'عرض إيجار', 'عقد', 'استحقاقات وتحصيل', 'تجديد أو إخلاء'],
      },
      caption: 'يدعم الحل الوحدات السكنية والتجارية ضمن التجربة نفسها.',
    },

    // Property management
    property: {
      overline: 'إدارة الأملاك والملاك',
      title: 'إدارة عقارات الشركة وعقارات الملاك ضمن تجربة شفافة',
      items: [
        { title: 'محفظة المالك', desc: 'الأصول والوحدات في مساحة واحدة.' },
        { title: 'الإشغال والعقود', desc: 'حالة الوحدات وعقود الإيجار وتواريخ التجديد.' },
        { title: 'التحصيل والاستحقاقات', desc: 'الإيرادات والمصروفات وصافي المستحق.' },
        { title: 'كشوف الحساب', desc: 'كشوف دورية وتوزيعات للمالك.' },
        { title: 'الصيانة', desc: 'طلبات الصيانة والموافقات والفواتير.' },
        { title: 'المستندات', desc: 'العقود والمستندات محفوظة في سجل الوحدة.' },
      ],
      text: 'يرى كل مالك أصوله وبياناته فقط، بينما تدير الشركة المحفظة من مساحة موحدة وصلاحيات واضحة.',
    },

    // External brokers
    brokers: {
      overline: 'المسوقون الخارجيون',
      title: 'وسّع شبكة البيع من دون أن تفقد السيطرة على العميل والعمولة',
      items: [
        'رؤية المخزون المصرح به فقط.',
        'إنشاء رابط مشاركة مرتبط بمصدر المسوق.',
        'تسجيل العميل وحمايته ضمن مدة محددة.',
        'متابعة الزيارة والعرض والحجز.',
        'متابعة العمولة المتوقعة والمستحقة والمدفوعة.',
      ],
      punch: 'الشركة تمنع تضارب العملاء وتعرف مصدر كل فرصة.',
    },

    // Maintenance
    maintenance: {
      overline: 'الصيانة والتشغيل',
      title: 'البلاغ يتحول إلى عمل موثق… لا محادثة يصعب الرجوع إليها',
      steps: ['بلاغ', 'تشخيص', 'عروض أسعار', 'موافقة المالك', 'أمر عمل', 'تنفيذ وفاتورة', 'إغلاق وتقييم'],
      text: 'الصور والقرارات والتكاليف محفوظة ضمن سجل الوحدة، ويظهر أثر المصروف مباشرة في كشف المالك.',
    },

    // Four experiences
    experiences: {
      overline: 'التجارب الأربع',
      title: 'كل طرف يرى ما يحتاجه… والجميع يعمل على الحقيقة نفسها',
      items: [
        { role: 'العميل النهائي', desc: 'البحث والاختيار والاستفسار والزيارة.' },
        { role: 'الشركة العقارية', desc: 'المخزون والمبيعات والتأجير والتشغيل.' },
        { role: 'المالك والمسوق', desc: 'صلاحيات وتجربة مخصصة.' },
        { role: 'NEXA', desc: 'التهيئة والهوية والبيانات والصلاحيات والدعم.' },
      ],
    },

    // Value
    value: {
      overline: 'القيمة المضافة',
      title: 'القيمة تمتد من تجربة العميل إلى دورة الأصل كاملة',
      items: [
        { role: 'الإدارة', desc: 'رؤية موحدة للمخزون والإيراد والتشغيل.' },
        { role: 'المبيعات والتسويق', desc: 'استفسارات مرتبطة بوحدات ومصادر قابلة للقياس.' },
        { role: 'التأجير وإدارة الأملاك', desc: 'عقود وتحصيل وتجديد وصيانة في سياق واحد.' },
        { role: 'العملاء والملاك', desc: 'شفافية واختيار أسهل وتجربة تحمل هوية الشركة.' },
      ],
    },

    // Why NEXA
    why: {
      overline: 'لماذا NEXA؟',
      title: 'لسنا موقع عرض إضافيًا، ولا نظامًا معلبًا يفرض طريقة عمله',
      pairs: [
        ['عرض منفصل عن المخزون', 'تجربة مرتبطة بحالة الوحدة'],
        ['استفسار عام', 'طلب يحمل المشروع والطابق والوحدة والمصدر'],
        ['أنظمة مستقلة', 'رحلة بيع وتأجير وتشغيل مترابطة'],
        ['تجربة واحدة للجميع', 'بوابات وصلاحيات لكل طرف'],
        ['تغيير شامل عالي المخاطر', 'إطلاق تدريجي وتكامل مع الواقع الحالي'],
      ],
    },

    // Method
    method: {
      overline: 'طريقة العمل',
      title: 'نبدأ من مشروع واضح، ثم نتوسع بناءً على قيمة مثبتة',
      phases: [
        { name: 'الاكتشاف', desc: 'فهم المحفظة والمخزون ورحلات العمل والأنظمة الحالية.' },
        { name: 'التصميم', desc: 'تجربة الشركة والمخططات والصلاحيات ومسارات العمل.' },
        { name: 'الإطلاق الأول', desc: 'تهيئة مشروع محدد وبياناته والفرق المعنية.' },
        { name: 'التوسع', desc: 'إضافة المشاريع والملاك والمسوقين والتكاملات والتقارير.' },
      ],
      note: 'يمكن دمج الحل مع الموقع والأنظمة الحالية بدل اشتراط استبدالها بالكامل.',
    },

    // Final CTA
    finalCta: {
      title: 'لنحوّل أحد مشاريعكم إلى تجربة يراها العميل وتديرها الشركة بوضوح',
      text: 'نراجع معكم مشروعًا واحدًا، وطريقة إدارة الوحدات والتوفر والاستفسارات، ثم نبني تصورًا مخصصًا لرحلة العميل والفريق.',
      cta: 'اطلب جلسة اكتشاف',
    },

    // SEO
    seo: {
      title: 'NEXA Real Estate OS | منصة تجربة وتشغيل عقاري',
      description: 'منصة عقارية تربط عرض المشاريع والمخططات التفاعلية بالمبيعات والتأجير وإدارة الأملاك وبوابات العملاء والملاك والمسوقين.',
    },
  },

  en: {
    slug: 'nexa-real-estate-os',
    productName: 'NEXA Real Estate OS',
    backToProducts: 'Back to Products',

    hero: {
      overline: 'Connected Real Estate Platform',
      title: 'Turn every real estate project into a connected sales and operations experience',
      text: 'NEXA Real Estate OS connects project discovery, buildings, floors, and units with inquiries, sales, leasing, and property operations — giving teams and customers one clear source of information.',
      cta1: 'Request a project concept',
      cta2: 'See how it works',
      chipMap: 'Project map',
      chipUnit: 'Unit A-201',
      chipStatus: 'Available',
    },

    problem: {
      overline: 'The problem',
      title: 'Customers see one project… while companies manage disconnected pieces',
      lead: 'When the website is separate from inventory, plans live in PDFs, prices sit in spreadsheets, and inquiries scatter across chats — the link between customer and unit is lost, and nobody really knows what is happening.',
      chain: [
        'Marketing site',
        'PDF floor plans',
        'Unit & price sheets',
        'WhatsApp inquiries',
        'Separate sales, leasing & operations systems',
      ],
      punchline: 'The problem is not missing data — it is the missing journey that ties it together.',
    },

    idea: {
      overline: 'The core idea',
      title: 'From map to contract… one context that never breaks',
      journey: [
        'Map',
        'Project',
        'Building & floor',
        'Unit',
        'Inquiry & visit',
        'Offer & booking',
        'Contract & collection',
        'Maintenance & owner reports',
      ],
      text: 'Every request carries the project, unit and source from the moment the customer picks a unit, and that context follows through sales, leasing and property management.',
    },

    search: {
      overline: 'Customer experience',
      title: 'Instead of asking "what is available?" — customers choose their unit while they understand it',
      mapLabel: 'Illustrative location — Al Narjis district, Riyadh',
      mapCaption: 'Illustrative data',
      unit: {
        code: 'A-201',
        rooms: '3 bedrooms',
        area: '148 m²',
        direction: 'North-East',
        price: 'SAR 1,420,000',
        status: 'Available',
      },
      hint: 'Hover the unit to see details',
      hintMobile: 'Tap the unit to see details',
    },

    inquiry: {
      overline: 'Inquiries',
      title: 'Sales knows what the customer picked before the first call',
      fields: [
        { label: 'Customer & source', value: 'Mohammed Alharbi — Google Ads' },
        { label: 'Project', value: 'Sidra Square — Al Narjis' },
        { label: 'Building & floor', value: 'A — Floor 2' },
        { label: 'Unit', value: 'A-201 (North-East)' },
        { label: 'Price & area', value: 'SAR 1,420,000 — 148 m²' },
        { label: 'Also viewed', value: 'A-201, A-305, B-102' },
        { label: 'Assignee', value: 'Sara Alotaibi' },
        { label: 'Next action', value: 'Site visit — Thursday 5:00 PM' },
      ],
      valueTitle: 'What this means in practice',
      values: [
        'Clearer follow-up for teams and customers.',
        'More accurate lead routing by project and source.',
        'Real visibility into which sources drive opportunities.',
        'Fewer conflicts around unit availability across teams.',
      ],
    },

    tracks: {
      overline: 'Sales & leasing',
      title: 'The same unit flows from marketing to contract and collection',
      sale: {
        label: 'Sales track',
        steps: ['Inquiry', 'Qualification', 'Visit', 'Offer', 'Booking', 'Contract', 'Handover'],
      },
      lease: {
        label: 'Leasing track',
        steps: ['Request', 'Review', 'Lease offer', 'Contract', 'Billing & collection', 'Renewal or exit'],
      },
      caption: 'Residential and commercial units live in the same experience.',
    },

    property: {
      overline: 'Property & owner management',
      title: 'Manage company assets and third-party owners in one transparent experience',
      items: [
        { title: 'Owner portfolio', desc: 'Assets and units in one workspace.' },
        { title: 'Occupancy & contracts', desc: 'Unit status, lease contracts, renewal dates.' },
        { title: 'Collections & receivables', desc: 'Revenue, expenses and net due.' },
        { title: 'Statements', desc: 'Periodic statements and distributions.' },
        { title: 'Maintenance', desc: 'Requests, approvals and invoices.' },
        { title: 'Documents', desc: 'Contracts and files stored per unit.' },
      ],
      text: 'Every owner sees only their own assets, while the company runs the portfolio from one unified workspace with clear permissions.',
    },

    brokers: {
      overline: 'External brokers',
      title: 'Expand your sales network without losing the customer or the commission',
      items: [
        'See only the inventory they are authorised for.',
        'Generate a shareable link tied to their source.',
        'Register and protect a lead for a defined window.',
        'Follow visits, offers and bookings.',
        'Track expected, due and paid commissions.',
      ],
      punch: 'The company prevents lead conflicts and always knows the source of every opportunity.',
    },

    maintenance: {
      overline: 'Maintenance & operations',
      title: 'Every report becomes a documented work order — not a chat that is hard to trace',
      steps: ['Report', 'Diagnosis', 'Quotes', 'Owner approval', 'Work order', 'Execution & invoice', 'Close & rate'],
      text: 'Photos, decisions and costs live inside the unit record, and the expense flows into the owner statement.',
    },

    experiences: {
      overline: 'Four experiences',
      title: 'Every stakeholder sees what they need — everyone works on the same truth',
      items: [
        { role: 'End customer', desc: 'Discovery, selection, inquiry and visit.' },
        { role: 'Real estate company', desc: 'Inventory, sales, leasing and operations.' },
        { role: 'Owner & broker', desc: 'Dedicated portals with tailored permissions.' },
        { role: 'NEXA', desc: 'Setup, branding, data, permissions and support.' },
      ],
    },

    value: {
      overline: 'Business value',
      title: 'Value spans from the customer experience to the full asset lifecycle',
      items: [
        { role: 'Management', desc: 'One view of inventory, revenue and operations.' },
        { role: 'Sales & marketing', desc: 'Inquiries linked to units and measurable sources.' },
        { role: 'Leasing & property mgmt', desc: 'Contracts, collections, renewals and maintenance in one context.' },
        { role: 'Customers & owners', desc: 'Transparency, easier choices and a branded experience.' },
      ],
    },

    why: {
      overline: 'Why NEXA?',
      title: 'We are neither yet another listings site nor a rigid packaged system',
      pairs: [
        ['Listing detached from inventory', 'Experience tied to real unit status'],
        ['Generic inquiry', 'Request carrying project, floor, unit and source'],
        ['Isolated systems', 'Connected sales, leasing and operations journey'],
        ['One-size-fits-all experience', 'Portals and permissions per stakeholder'],
        ['High-risk full replacement', 'Phased rollout integrating with your current stack'],
      ],
    },

    method: {
      overline: 'How we work',
      title: 'We start with a clear project and expand on proven value',
      phases: [
        { name: 'Discovery', desc: 'Understand portfolio, inventory, workflows and current systems.' },
        { name: 'Design', desc: 'Company experience, plans, permissions and operational flows.' },
        { name: 'First launch', desc: 'Configure a specific project, its data and the involved teams.' },
        { name: 'Scale', desc: 'Add projects, owners, brokers, integrations and reports.' },
      ],
      note: 'The platform integrates with your existing website and systems rather than forcing a full replacement.',
    },

    finalCta: {
      title: "Let's turn one of your projects into an experience customers can see and teams can run clearly",
      text: 'We review one project with you — how you manage units, availability and inquiries — then build a tailored concept for the customer and team journey.',
      cta: 'Request a discovery session',
    },

    seo: {
      title: 'NEXA Real Estate OS | Connected Real Estate Experience & Operations',
      description: 'A white-label real estate platform connecting interactive project discovery and inventory with sales, leasing, property management, and stakeholder portals.',
    },
  },
};
